import React, { useState, useEffect } from 'react'
import { 
  FileText, 
  CreditCard, 
  User, 
  Calendar,
  DollarSign,
  Download,
  Eye,
  Printer,
  Filter,
  Search,
  Plus,
  Edit,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Receipt,
  TrendingUp,
  X
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { posService } from '../services/posService'
import LoadingSpinner from '../components/LoadingSpinner'
import { cn } from '../utils/cn'
import { formatRand, getVoucherLoyalty } from '../utils/voucherLoyalty'
import { businessProfileService } from '@/services/businessProfileService'
import { PDFGenerator } from '@/utils/pdfGenerator'
import { BranchService } from '@/services/branchService'
import { customerService } from '@/services/firebase'
import toast from 'react-hot-toast'

export default function SalesInvoicesPage() {
  const { currentUser, userProfile, role } = useAuth()
  const { currentBranch, isViewingAllBranches } = useBranch()
  const isOwner = String(role || userProfile?.role || '').toLowerCase() === 'owner'

  const PAGE_SIZE = 25
  const [invoicePage, setInvoicePage] = useState(1)
  
  const money = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n.toFixed(2) : '0.00'
  }

  const qty = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }

  const asDate = (value) => {
    if (!value) return null
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      const d = value.toDate()
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
    }
    const d = new Date(value)
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
  }

  const invoiceDisplayId = (inv) => {
    const candidates = [
      inv?.invoiceNumber,
      inv?.businessId,
      // Legacy field stored on the Firestore doc before we remapped `id`
      typeof inv?.invoiceNo === 'string' ? inv.invoiceNo : null,
    ].filter(Boolean)
    const numbered = candidates.find(
      (v) => /^INV[-_]/i.test(String(v)) || String(v).startsWith('CN-')
    )
    if (numbered) return numbered
    if (candidates[0] && candidates[0] !== inv?.firestoreId && candidates[0] !== inv?.id) {
      return candidates[0]
    }
    // Last resort: never prefer a bare Firestore auto-id if we have something better
    if (inv?.invoiceNumber) return inv.invoiceNumber
    return candidates[0] || 'N/A'
  }

  const invoiceDate = (inv) =>
    asDate(inv?.issueDate) || asDate(inv?.createdAt) || asDate(inv?.date) || asDate(inv?.updatedAt) || null

  const invoiceDueDate = (inv) => asDate(inv?.dueDate) || null

  const normalizeInvoiceItems = (inv) => {
    const items = Array.isArray(inv?.items) ? inv.items : []
    return items.map((item) => {
      const quantity = qty(item.quantity)
      const unitPrice = Number(item.unitPrice ?? item.price ?? 0)
      const safeUnit = Number.isFinite(unitPrice) ? unitPrice : 0
      const lineTotal = Number(item.total)
      return {
        name: item.name || item.description || 'Item',
        quantity,
        unitPrice: safeUnit,
        total: Number.isFinite(lineTotal) ? lineTotal : quantity * safeUnit,
      }
    })
  }

  const toInvoicePdfData = (invoice, extras = {}) => {
    const issueDate = invoice?.issueDate || invoice?.date || invoice?.createdAt || new Date().toISOString()
    const items = Array.isArray(invoice?.items) ? invoice.items : []

    const mappedItems = items.map((item) => {
      const quantity = qty(item.quantity)
      const unitPrice = Number(item.unitPrice ?? item.price)
      const safeUnit = Number.isFinite(unitPrice) ? unitPrice : 0
      return {
        description: item.name || item.description || 'Item',
        quantity,
        unitPrice: safeUnit,
        total: quantity * safeUnit,
      }
    })

    const subtotal = Number(invoice?.subtotal)
    const tax = Number(invoice?.tax)
    const discountVal =
      invoice?.discount && typeof invoice.discount === 'object'
        ? Number(invoice.discount.amount)
        : Number(invoice?.discount)
    const total = Number(invoice?.total)

    return {
      id: invoice?.invoiceNumber || invoice?.businessId || invoice?.id || 'invoice',
      issueDate,
      customerName: invoice?.customerName || 'Customer',
      customerId: invoice?.customerId || '',
      customerPhone: invoice?.customerPhone || extras.customerPhone || '',
      customerCode: extras.customerCode || invoice?.customerCode || '',
      branchId: invoice?.branchId || '',
      items: mappedItems,
      subtotal: Number.isFinite(subtotal) ? subtotal : mappedItems.reduce((s, it) => s + it.total, 0),
      tax: Number.isFinite(tax) ? tax : 0,
      discount: Number.isFinite(discountVal) ? discountVal : 0,
      total:
        Number.isFinite(total)
          ? total
          : mappedItems.reduce((s, it) => s + it.total, 0),
    }
  }

  async function resolveInvoiceSlipContext(invoice) {
    const businessProfile = await businessProfileService.get()
    let branch = null
    let customerCode = invoice?.customerCode || ''
    let customerPhone = invoice?.customerPhone || ''

    if (invoice?.branchId) {
      try {
        branch = await BranchService.getBranchById(invoice.branchId)
      } catch (e) {
        console.warn('Could not load branch for invoice slip:', e)
      }
    }

    if (invoice?.customerId && invoice.customerId !== 'walk-in' && !customerCode) {
      try {
        const customer = await customerService.getCustomerById(invoice.customerId)
        customerCode = customer?.customerCode || ''
        customerPhone = customerPhone || customer?.phone || ''
        if (!customerCode && customer?.id) {
          customerCode = await customerService.ensureCustomerCode(customer.id)
        }
      } catch (e) {
        console.warn('Could not load customer code for invoice slip:', e)
      }
    }

    return {
      businessProfile,
      branch,
      pdfData: toInvoicePdfData(invoice, { customerCode, customerPhone }),
    }
  }

  const discountAmountOf = (inv) => {
    if (inv?.discount && typeof inv.discount === 'object') {
      return Number(inv.discount.amount) || 0
    }
    return Number(inv?.discount) || 0
  }

  const [activeTab, setActiveTab] = useState('invoices')
  const [invoices, setInvoices] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [customerFilter, setCustomerFilter] = useState('all')
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')
  
  useEffect(() => {
    loadData()
  }, [currentBranch?.id, dateFilter])

  const loadData = async () => {
    setLoading(true)
    try {
      const branchId =
        currentBranch && !isViewingAllBranches?.() ? currentBranch.id : null
      const [invoicesData, customersData] = await Promise.all([
        posService.getInvoices(branchId),
        posService.getCustomers()
      ])
      setInvoices(invoicesData)
      setCustomers(customersData)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'invoices', name: 'Invoices', icon: FileText },
    { id: 'accounts', name: 'Customer Accounts', icon: User },
    { id: 'credits', name: 'Credits & Refunds', icon: CreditCard },
    { id: 'reports', name: 'Sales Reports', icon: DollarSign }
  ]

  const invoiceStatuses = [
    { value: 'all', label: 'All Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' }
  ]

  const filteredInvoices = invoices.filter(invoice => {
    const q = searchTerm.toLowerCase()
    const displayId = String(invoiceDisplayId(invoice) || '').toLowerCase()
    const matchesSearch =
      !q ||
      displayId.includes(q) ||
      String(invoice.invoiceNumber || '').toLowerCase().includes(q) ||
      String(invoice.customerName || '').toLowerCase().includes(q) ||
      String(invoice.customerPhone || '').includes(searchTerm)
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Latest first
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    const ad = invoiceDate(a)?.getTime?.() || 0
    const bd = invoiceDate(b)?.getTime?.() || 0
    return bd - ad
  })

  const invoiceTotalPages = Math.max(1, Math.ceil(sortedInvoices.length / PAGE_SIZE))
  const pagedInvoices = sortedInvoices.slice((invoicePage - 1) * PAGE_SIZE, invoicePage * PAGE_SIZE)

  useEffect(() => {
    setInvoicePage(1)
  }, [searchTerm, statusFilter, dateFilter, customerFilter, currentBranch?.id])

  useEffect(() => {
    if (invoicePage > invoiceTotalPages) setInvoicePage(invoiceTotalPages)
  }, [invoicePage, invoiceTotalPages])

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice)
    setShowInvoiceModal(true)
  }

  const openEditInvoice = (invoice) => {
    if (!isOwner) {
      toast.error('Only the owner can edit transactions')
      return
    }
    setSelectedInvoice(invoice)
    setEditForm({
      customerName: invoice.customerName || '',
      customerPhone: invoice.customerPhone || '',
      customerEmail: invoice.customerEmail || '',
      paymentMethod: invoice.paymentMethod || 'cash',
      status: invoice.status || 'paid',
      discountAmount: discountAmountOf(invoice),
      editReason: '',
      items: normalizeInvoiceItems(invoice).map((it) => ({
        name: it.name,
        description: it.name,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        total: it.total,
      })),
    })
    setShowEditModal(true)
  }

  const updateEditItem = (index, field, value) => {
    setEditForm((prev) => {
      if (!prev) return prev
      const items = [...prev.items]
      const row = { ...items[index], [field]: value }
      const quantity = Number(row.quantity) || 0
      const unitPrice = Number(row.unitPrice) || 0
      row.total = quantity * unitPrice
      items[index] = row
      return { ...prev, items }
    })
  }

  const addEditItem = () => {
    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            items: [
              ...prev.items,
              { name: '', description: '', quantity: 1, unitPrice: 0, total: 0 },
            ],
          }
        : prev
    )
  }

  const removeEditItem = (index) => {
    setEditForm((prev) => {
      if (!prev) return prev
      const items = prev.items.filter((_, i) => i !== index)
      return { ...prev, items: items.length ? items : prev.items }
    })
  }

  const handleSaveEdit = async () => {
    if (!isOwner || !selectedInvoice || !editForm) return
    if (!editForm.customerName?.trim()) {
      toast.error('Customer name is required')
      return
    }
    if (!editForm.items?.length) {
      toast.error('Add at least one line item')
      return
    }
    setSavingEdit(true)
    try {
      await posService.updateInvoice(selectedInvoice, editForm, {
        uid: currentUser?.uid,
        name: userProfile?.name || userProfile?.displayName,
        email: currentUser?.email,
      })
      toast.success('Transaction updated')
      setShowEditModal(false)
      setEditForm(null)
      setSelectedInvoice(null)
      setShowInvoiceModal(false)
      await loadData()
    } catch (error) {
      console.error('Error updating invoice:', error)
      toast.error(error?.message || 'Failed to update transaction')
    } finally {
      setSavingEdit(false)
    }
  }

  const handlePrintInvoice = async (invoice) => {
    try {
      const { businessProfile, branch, pdfData } = await resolveInvoiceSlipContext(invoice)
      const pdfBlob = await PDFGenerator.generateA4Invoice(pdfData, businessProfile, branch)
      await PDFGenerator.printThermalReceipt(pdfBlob)
    } catch (error) {
      console.error('Error printing invoice:', error)
      alert('Failed to print invoice')
    }
  }

  const handleDownloadInvoice = async (invoice) => {
    try {
      const { businessProfile, branch, pdfData } = await resolveInvoiceSlipContext(invoice)
      const pdfBlob = await PDFGenerator.generateA4Invoice(pdfData, businessProfile, branch)
      PDFGenerator.downloadPDF(pdfBlob, `invoice-${invoiceDisplayId(invoice)}.pdf`)
    } catch (error) {
      console.error('Error downloading invoice:', error)
      alert('Failed to download invoice')
    }
  }

  const handleCreateCredit = async () => {
    if (!selectedInvoice || !creditAmount || !creditReason) {
      alert('Please fill in all credit details')
      return
    }

    try {
      await posService.createCredit({
        invoiceId: selectedInvoice.id,
        customerId: selectedInvoice.customerId,
        amount: parseFloat(creditAmount),
        reason: creditReason,
        createdBy: currentUser?.uid,
        branchId: currentBranch?.id,
      })
      
      setShowCreditModal(false)
      setCreditAmount('')
      setCreditReason('')
      loadData() // Reload data
      alert('Credit created successfully')
    } catch (error) {
      console.error('Error creating credit:', error)
      alert('Failed to create credit')
    }
  }

  const getTotalRevenue = () => {
    return invoices.reduce((sum, invoice) => sum + invoice.total, 0);
  };

  const getMonthlyRevenue = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return invoices.filter(invoice => {
      const d = invoiceDate(invoice)
      if (!d) return false
      return d >= startOfMonth && d <= endOfMonth;
    }).reduce((sum, invoice) => sum + invoice.total, 0);
  };

  const getAverageInvoice = () => {
    if (invoices.length === 0) return 0;
    return invoices.reduce((sum, invoice) => sum + invoice.total, 0) / invoices.length;
  };

  const getUniqueCustomers = () => {
    const unique = new Set();
    invoices.forEach(invoice => {
      if (invoice.customerName) {
        unique.add(invoice.customerName);
      }
    });
    return Array.from(unique).sort();
  };

  const renderInvoicesTab = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {invoiceStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagedInvoices.map(invoice => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{invoiceDisplayId(invoice)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.customerName}</div>
                    <div className="text-sm text-gray-500">{invoice.customerPhone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoiceDate(invoice)?.toLocaleDateString('en-ZA') || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    R{money(invoice.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewInvoice(invoice)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handlePrintInvoice(invoice)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownloadInvoice(invoice)}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {invoice.status === 'paid' && (
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice)
                          setShowCreditModal(true)
                        }}
                        className="text-orange-600 hover:text-orange-900"
                      >
                        <CreditCard className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderAccountsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {customers.map(customer => (
          <div key={customer.id} className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">{customer.name}</h3>
                  <p className="text-sm text-gray-500">{customer.phone}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Spent:</span>
                <span className="font-medium">R{money(customer.totalSpent)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Visits:</span>
                <span className="font-medium">{customer.totalVisits || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Voucher progress:</span>
                <span className="font-medium">{getVoucherLoyalty(customer).progressLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Voucher balance:</span>
                <span className="font-medium">{formatRand(getVoucherLoyalty(customer).balance)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Account Credit:</span>
                <span className="font-medium text-green-600">R{money(customer.accountCredit)}</span>
              </div>
            </div>
            
            <div className="mt-4 flex space-x-2">
              <button className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                <Edit className="h-4 w-4 inline mr-1" />
                Edit
              </button>
              <button className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                <Plus className="h-4 w-4 inline mr-1" />
                Credit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderCreditsTab = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold mb-4">Credits & Refunds</h3>
      <p className="text-gray-600">Credit management functionality coming soon...</p>
    </div>
  )

  const renderReportsTab = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold mb-4">Sales Reports</h3>
      <p className="text-gray-600">Detailed sales reporting functionality coming soon...</p>
    </div>
  )

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <Receipt className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Sales Invoices
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            View and manage all sales invoices and transactions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mr-4 text-emerald-700 dark:text-emerald-400">
                <Receipt className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4 text-primary">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">R{money(getTotalRevenue())}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-4 text-violet-700 dark:text-violet-400">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">R{money(getMonthlyRevenue())}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mr-4 text-amber-800 dark:text-amber-400">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Invoice</p>
                <p className="text-2xl font-bold text-gray-900">R{money(getAverageInvoice())}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div className="relative">
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Customers</option>
                {getUniqueCustomers().map(customer => (
                  <option key={customer} value={customer}>{customer}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{invoiceDisplayId(invoice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{invoice.customerName || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{invoice.customerPhone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoiceDate(invoice)?.toLocaleDateString('en-ZA') || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      R{money(invoice.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors duration-200"
                        title="View Invoice"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handlePrintInvoice(invoice)}
                        className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50 transition-colors duration-200"
                        title="Print Invoice"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadInvoice(invoice)}
                        className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors duration-200"
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => openEditInvoice(invoice)}
                          className="text-violet-600 hover:text-violet-900 p-1 rounded hover:bg-violet-50 transition-colors duration-200"
                          title="Edit transaction (owner)"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {invoice.status === 'paid' && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice)
                            setShowCreditModal(true)
                          }}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50 transition-colors duration-200"
                          title="Create Credit"
                        >
                          <CreditCard className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {sortedInvoices.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Receipt className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No invoices found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || dateFilter || statusFilter !== 'all' || customerFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'No sales invoices have been created yet.'
              }
            </p>
          </div>
        )}

        {/* Pagination */}
        {sortedInvoices.length > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-4">
            <p className="text-xs text-muted-foreground">
              Showing {(invoicePage - 1) * PAGE_SIZE + 1}-{Math.min(invoicePage * PAGE_SIZE, sortedInvoices.length)} of{' '}
              {sortedInvoices.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                onClick={() => setInvoicePage((p) => Math.max(1, p - 1))}
                disabled={invoicePage <= 1}
              >
                Prev
              </button>
              <span className="text-xs text-muted-foreground">
                Page {invoicePage} / {invoiceTotalPages}
              </span>
              <button
                type="button"
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                onClick={() => setInvoicePage((p) => Math.min(invoiceTotalPages, p + 1))}
                disabled={invoicePage >= invoiceTotalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && showInvoiceModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mr-3 text-emerald-700 dark:text-emerald-400">
                    <Receipt className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
                </div>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Invoice Header */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-primary/5 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Invoice Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice #:</span>
                        <span className="font-medium">{invoiceDisplayId(selectedInvoice)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{invoiceDate(selectedInvoice)?.toLocaleDateString('en-ZA') || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Due Date:</span>
                        <span className="font-medium">{invoiceDueDate(selectedInvoice)?.toLocaleDateString('en-ZA') || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={cn(
                          "font-medium",
                          selectedInvoice.status === 'paid' ? "text-green-600" : 
                          selectedInvoice.status === 'pending' ? "text-yellow-600" : "text-red-600"
                        )}>
                          {selectedInvoice.status?.charAt(0).toUpperCase() + selectedInvoice.status?.slice(1) || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-500/5 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Customer Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium">{selectedInvoice.customerName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedInvoice.customerEmail || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedInvoice.customerPhone || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">Invoice Items</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedInvoice.items?.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.name || item.description || 'Item'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{qty(item.quantity)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              R{money(item.unitPrice ?? item.price)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              R{money(item.total ?? (qty(item.quantity) * qty(item.unitPrice ?? item.price)))}
                            </td>
                          </tr>
                        ))}
                        {selectedInvoice.items?.length === 0 && (
                          <tr>
                            <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">No items</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-violet-500/5 rounded-2xl p-6">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">R{money(selectedInvoice.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium">R{money(selectedInvoice.tax)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Discount:</span>
                        <span className="font-medium">R{money(discountAmountOf(selectedInvoice))}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total:</span>
                          <span>R{money(selectedInvoice.total)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap justify-end gap-3">
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowInvoiceModal(false)
                        openEditInvoice(selectedInvoice)
                      }}
                      className="rounded-xl border border-violet-300 bg-violet-50 px-6 py-3 font-semibold text-violet-800 transition hover:bg-violet-100 flex items-center"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit transaction
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                    className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => handlePrintInvoice(selectedInvoice)}
                    className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 flex items-center"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false)
                      setSelectedInvoice(null)
                    }}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Owner edit transaction modal */}
      {showEditModal && selectedInvoice && editForm && isOwner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="space-y-5 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Edit transaction</h3>
                  <p className="text-sm text-muted-foreground">
                    #{invoiceDisplayId(selectedInvoice)} · Owner only
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditForm(null)
                  }}
                  className="rounded-xl p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Customer name</label>
                  <input
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.customerName}
                    onChange={(e) => setEditForm((p) => ({ ...p, customerName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.customerPhone}
                    onChange={(e) => setEditForm((p) => ({ ...p, customerPhone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.customerEmail}
                    onChange={(e) => setEditForm((p) => ({ ...p, customerEmail: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Payment method</label>
                  <select
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.paymentMethod}
                    onChange={(e) => setEditForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="eft">EFT</option>
                    <option value="account">On account</option>
                    <option value="voucher">Voucher</option>
                    <option value="subscription">Subscription</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Discount (R)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                    value={editForm.discountAmount}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, discountAmount: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Line items</label>
                  <button
                    type="button"
                    onClick={addEditItem}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    + Add item
                  </button>
                </div>
                <div className="space-y-2">
                  {editForm.items.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 gap-2 rounded-lg border border-border p-2"
                    >
                      <input
                        className="col-span-5 rounded border border-input px-2 py-1.5 text-sm"
                        placeholder="Description"
                        value={item.name}
                        onChange={(e) => updateEditItem(index, 'name', e.target.value)}
                      />
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="col-span-2 rounded border border-input px-2 py-1.5 text-sm"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateEditItem(index, 'quantity', e.target.value)}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="col-span-2 rounded border border-input px-2 py-1.5 text-sm"
                        placeholder="Price"
                        value={item.unitPrice}
                        onChange={(e) => updateEditItem(index, 'unitPrice', e.target.value)}
                      />
                      <div className="col-span-2 flex items-center text-sm font-medium tabular-nums">
                        R{money(item.total)}
                      </div>
                      <button
                        type="button"
                        className="col-span-1 text-red-600 hover:text-red-800"
                        onClick={() => removeEditItem(index)}
                        title="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  New total:{' '}
                  <span className="font-semibold text-foreground">
                    R
                    {money(
                      editForm.items.reduce((s, it) => s + Number(it.total || 0), 0) -
                        (Number(editForm.discountAmount) || 0)
                    )}
                  </span>
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Reason for edit
                </label>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                  placeholder="e.g. Corrected wrong wash package"
                  value={editForm.editReason}
                  onChange={(e) => setEditForm((p) => ({ ...p, editReason: e.target.value }))}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded-xl bg-gray-100 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditForm(null)
                  }}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-60"
                  onClick={() => void handleSaveEdit()}
                  disabled={savingEdit}
                >
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {showCreditModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">Create Credit</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credit Amount (R)</label>
                  <input
                    type="number"
                    step="0.01"
                    max={selectedInvoice.total}
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Reason for credit/refund"
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowCreditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCredit}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Create Credit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 